/**
 * Qualified-lead referral program — pure helpers (no DB).
 *
 * Payable ONLY when BOTH exist in Veras:
 *   (1) completed meeting — call_bookings.status = completed
 *   (2) bank-statement-level proof of funds on file
 *
 * Pay date: 15th of the month AFTER both complete.
 * Held ≠ unpaid. Held = no W-9/W-8 or a gate was removed; held stays
 * OUT of the Grey Rock export.
 *
 * Deliberately separate from funded-deal commissions
 * (commission_rules / entries / payouts, trigger subscription_funded).
 */

export const LEAD_REFERRAL_REFERRER_TYPES = [
  "investor",
  "attorney",
  "agent",
] as const;
export type LeadReferralReferrerType =
  (typeof LEAD_REFERRAL_REFERRER_TYPES)[number];

export const LEAD_REFERRAL_BOUNTY_STATUSES = [
  "submitted",
  "duplicate",
  "in_progress",
  "junk",
  "payable",
  "held",
  "paid",
  "voided",
] as const;
export type LeadReferralBountyStatus =
  (typeof LEAD_REFERRAL_BOUNTY_STATUSES)[number];

export const DEFAULT_LEAD_REFERRAL_RATE_CENTS = 75_000;
export const JUNK_STRIKE_PAUSE_THRESHOLD = 3;

import {
  PARTNER_PROGRAM_ASSUMPTION_DEFAULTS,
  type PartnerProgramAssumptions,
} from "./partner-program-assumptions";

/** Brief 2 Schedule B. The sentence already says "days" after this number. */
export const LEAD_TERMINATION_NOTICE_DAYS =
  PARTNER_PROGRAM_ASSUMPTION_DEFAULTS.leadTerminationNoticeDays;

/** Calendar date as YYYY-MM-DD in UTC. */
export function toUtcDateString(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Pay date = 15th of the month AFTER the month in which both gates
 * completed (`earnedAt`).
 *
 * Examples (UTC):
 *   2026-01-20 → 2026-02-15
 *   2026-02-01 → 2026-03-15
 *   2026-12-31 → 2027-01-15
 */
/** Pay day clamped to the month's last calendar day (31 → 28/29/30 when needed). */
export function clampLeadPayDayInMonth(
  year: number,
  monthIndex: number,
  payDay: number,
): number {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return Math.min(payDay, lastDay);
}

export function computeLeadReferralDueDate(
  earnedAt: Date,
  assumptions?: PartnerProgramAssumptions,
): Date {
  const payDay = (assumptions ?? PARTNER_PROGRAM_ASSUMPTION_DEFAULTS).leadPayDayOfMonth;
  const y = earnedAt.getUTCFullYear();
  const m = earnedAt.getUTCMonth(); // 0-based
  const nextMonth = new Date(Date.UTC(y, m + 1, 1));
  const ty = nextMonth.getUTCFullYear();
  const tm = nextMonth.getUTCMonth();
  const day = clampLeadPayDayInMonth(ty, tm, payDay);
  return new Date(Date.UTC(ty, tm, day, 12, 0, 0));
}

export function computeLeadReferralDueDateString(
  earnedAt: Date,
  assumptions?: PartnerProgramAssumptions,
): string {
  return toUtcDateString(computeLeadReferralDueDate(earnedAt, assumptions));
}

/**
 * Next upcoming configured pay day relative to `asOf` (UTC).
 * If `asOf` is on or before that day this month, this month wins (this-run includes today).
 * Otherwise the following month's pay day (clamped to month length).
 */
export function nextLeadPayDueDate(
  asOf: Date = new Date(),
  payDayOfMonth: number = PARTNER_PROGRAM_ASSUMPTION_DEFAULTS.leadPayDayOfMonth,
): Date {
  const y = asOf.getUTCFullYear();
  const m = asOf.getUTCMonth();
  const d = asOf.getUTCDate();
  const thisMonthPayDay = clampLeadPayDayInMonth(y, m, payDayOfMonth);
  if (d <= thisMonthPayDay) {
    return new Date(Date.UTC(y, m, thisMonthPayDay, 12, 0, 0));
  }
  const nextMonth = new Date(Date.UTC(y, m + 1, 1));
  const ty = nextMonth.getUTCFullYear();
  const tm = nextMonth.getUTCMonth();
  const day = clampLeadPayDayInMonth(ty, tm, payDayOfMonth);
  return new Date(Date.UTC(ty, tm, day, 12, 0, 0));
}

export function nextLeadPayDueDateString(
  asOf: Date = new Date(),
  payDayOfMonth: number = PARTNER_PROGRAM_ASSUMPTION_DEFAULTS.leadPayDayOfMonth,
): string {
  return toUtcDateString(nextLeadPayDueDate(asOf, payDayOfMonth));
}

/** @deprecated Prefer nextLeadPayDueDate — kept for tests and call sites on the 15th default. */
export function nextFifteenthDueDate(asOf: Date = new Date()): Date {
  return nextLeadPayDueDate(asOf, 15);
}

export function nextFifteenthDueDateString(asOf: Date = new Date()): string {
  return nextLeadPayDueDateString(asOf, 15);
}

export interface QualificationGates {
  meetingCompleted: boolean;
  meetingCompletedAt: Date | null;
  pofOnFile: boolean;
  pofOnFileAt: Date | null;
}

/**
 * Both gates must be true for a bounty to become payable/held.
 * earnedAt = max(meetingCompletedAt, pofOnFileAt).
 */
export function evaluateQualification(gates: QualificationGates): {
  bothComplete: boolean;
  earnedAt: Date | null;
  dueDate: Date | null;
} {
  if (
    !gates.meetingCompleted ||
    !gates.pofOnFile ||
    !gates.meetingCompletedAt ||
    !gates.pofOnFileAt
  ) {
    return { bothComplete: false, earnedAt: null, dueDate: null };
  }
  const earnedAt =
    gates.meetingCompletedAt.getTime() >= gates.pofOnFileAt.getTime()
      ? gates.meetingCompletedAt
      : gates.pofOnFileAt;
  return {
    bothComplete: true,
    earnedAt,
    dueDate: computeLeadReferralDueDate(earnedAt),
  };
}

/**
 * After both gates are met: payable if tax form on file, else held.
 * Missing meeting or POF → not payable (caller keeps in_progress / held).
 *
 * Callers must not stamp amount/due while held for missing tax — see
 * `reevaluateBountyQualification` (Held = no rateSnapshot / dueDate).
 */
export function resolvePostQualificationStatus(opts: {
  bothComplete: boolean;
  hasTaxForm: boolean;
  gateRemovedAfterQualification?: boolean;
}): "payable" | "held" | "in_progress" {
  if (opts.gateRemovedAfterQualification) return "held";
  if (!opts.bothComplete) return "in_progress";
  return opts.hasTaxForm ? "payable" : "held";
}

/**
 * $750 payee tax: W-9 only for US partners (primary country of operation).
 * Coverage that merely includes the US does not make a foreign firm a US payee.
 * Non-agent referrers still need a tax document on file.
 *
 * US individual (`person`) and US business (`entity`) share this gate —
 * personVsEntity is Grey Rock export naming only, not a tax-form fork.
 */
export function taxFormRequiredForPayee(opts: {
  referrerType: LeadReferralReferrerType;
  isUsPayee: boolean;
}): boolean {
  if (opts.referrerType === "agent") return opts.isUsPayee;
  return true;
}

/** Primary coverage country (apply-form Country) is the payee jurisdiction. */
export function isUsPayeeFromCoverage(
  countries: ReadonlyArray<{ code: string; isPrimary?: boolean }>,
): boolean {
  const primary =
    countries.find((c) => c.isPrimary) ?? countries[0] ?? null;
  return (primary?.code ?? "").toUpperCase() === "US";
}

/** This-run export eligibility: payable only, due on the selected 15th. */
export function isInThisRunExport(opts: {
  status: LeadReferralBountyStatus;
  dueDate: string | Date | null;
  runDueDate: string;
}): boolean {
  if (opts.status !== "payable") return false;
  if (!opts.dueDate) return false;
  const due =
    typeof opts.dueDate === "string"
      ? opts.dueDate.slice(0, 10)
      : toUtcDateString(opts.dueDate);
  return due === opts.runDueDate.slice(0, 10);
}

export function csvEscape(value: string | number | null | undefined): string {
  let s = value == null ? "" : String(value);
  // Neutralize spreadsheet formula injection (=, +, -, @, tab, CR).
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Grey Rock CSV — one row per lead. Header entity: Veras Capital, LLC.
 * NO TIN (TIN stays in Grey Rock vendor file).
 */
export function buildGreyRockCsv(rows: Array<{
  referrerLegalName: string;
  personVsEntity: "person" | "entity";
  amountCents: number;
  earnedDate: string;
  dueDate: string;
  leadId: string;
  rateSnapshotCents: number;
  meetingId: string;
  pofFileId: string;
  status: string;
}>): string {
  const headerEntity = "Veras Capital, LLC";
  const cols = [
    "Referrer Legal Name",
    "Person vs Entity",
    "Amount",
    "Earned Date",
    "Due Date",
    "Lead ID",
    "Rate Snapshot",
    "Meeting ID",
    "POF File ID",
    "Status",
  ];
  const lines: string[] = [
    csvEscape(headerEntity),
    cols.map(csvEscape).join(","),
  ];
  for (const r of rows) {
    lines.push(
      [
        r.referrerLegalName,
        r.personVsEntity,
        (r.amountCents / 100).toFixed(2),
        r.earnedDate,
        r.dueDate,
        r.leadId,
        (r.rateSnapshotCents / 100).toFixed(2),
        r.meetingId,
        r.pofFileId,
        r.status,
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return lines.join("\n") + "\n";
}

export function junkStrikeCausesPause(strikeCount: number): boolean {
  return strikeCount >= JUNK_STRIKE_PAUSE_THRESHOLD;
}

/** Investor/attorney portal: never expose rate / paid / payment economics. */
export function stripLeadReferralBountyEconomics<T extends Record<string, unknown>>(
  bounty: T,
): Omit<
  T,
  | "rateSnapshotCents"
  | "paidAmountCents"
  | "paymentRef"
  | "paidAt"
  | "paidByUserId"
  | "heldReason"
> {
  const {
    rateSnapshotCents: _r,
    paidAmountCents: _a,
    paymentRef: _p,
    paidAt: _paidAt,
    paidByUserId: _paidBy,
    heldReason: _held,
    ...safe
  } = bounty as T & {
    rateSnapshotCents?: unknown;
    paidAmountCents?: unknown;
    paymentRef?: unknown;
    paidAt?: unknown;
    paidByUserId?: unknown;
    heldReason?: unknown;
  };
  return redactPartnerFacingDuplicateNotes(safe);
}

/**
 * Partner list/GET responses must not leak which directory caused a
 * duplicate (hub_contact / existing_user / …). Keep that only in audit.
 */
const DUPLICATE_CONFLICT_NOTES_RE =
  /\bduplicate:(hub_contact|existing_user|existing_agent|existing_bounty)\b/i;

export function redactPartnerFacingDuplicateNotes<T extends Record<string, unknown>>(
  bounty: T,
): T {
  const notes = bounty.notes;
  if (typeof notes !== "string" || !DUPLICATE_CONFLICT_NOTES_RE.test(notes)) {
    return bounty;
  }
  return { ...bounty, notes: "duplicate" };
}
